// ─────────────────────────────────────────────────────────────────────
// MEMBER DATA — pure derivations behind the mobile app's «my» screens.
//
// The app has no per-user accounts yet: the member is a player id (see
// useCurrentMember), and a BOOKING carries no player id at all — `bookedBy` is
// free text. So "my bookings" cannot be a filter; it is a UNION of the three
// places a player is actually linked to a booking:
//
//   1. a planned single match (plan.bookingId → booking, plan.team{1,2}.p{1,2}Id)
//   2. an activity registration (registrations[].playerId → bookings with
//      seriesId === activity.id)
//   3. the booking's own bookedBy name / e-mail matching the member
//
// Everything here is pure (no React, no Storage) so it is unit-testable, and
// all dates are local `YYYY-MM-DD` + `HH:MM` strings — never toISOString().
// ─────────────────────────────────────────────────────────────────────

// THE SIGN-UP RULES NOW LIVE IN THE ENGINE (#245). The server decides them too
// — whether there is room is what turns a registration into `confirmed` rather
// than `waitlist` — and two implementations of that would show the member a
// place the server did not give. Re-exported so every call site here is
// unchanged; this file stays pure, since the engine is pure too.
import {
  activitySignupClosedReason,
  activityWhen,
  type ActivityWhen,
  memberRegistration,
  activityCapacity,
  supportsIndividualSignup,
  registrationStatusFor,
  withoutMemberRegistration,
} from '@starpoint/engine';

export {
  activitySignupClosedReason,
  activityWhen,
  type ActivityWhen,
  memberRegistration,
  activityCapacity,
  supportsIndividualSignup,
  registrationStatusFor,
  withoutMemberRegistration,
};

export interface MemberLike {
  id: any;
  name?: string;
  email?: string;
}

/** Local calendar date as YYYY-MM-DD (never toISOString — that is UTC). */
export function todayLocalISO(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * The name the home screen greets you by: the FULL name when it fits on one
 * line of the big greeting, otherwise just the first name. Empty → «der»
 * («Hei, der»). The limit is in characters — the greeting is 28 px display
 * type on a ~360 px wide phone, where 18 characters plus the wave fit.
 */
export const GREETING_NAME_MAX = 18;
export function greetingName(name: string | null | undefined, maxLen = GREETING_NAME_MAX): string {
  const full = (name || '').trim().replace(/\s+/g, ' ');
  if (!full) return 'der';
  if (full.length <= maxLen) return full;
  return full.split(' ')[0];
}

/** Epoch ms for a local date + HH:MM (defaults to 00:00). */
export function localMs(date: string, time?: string | null): number {
  if (!date) return NaN;
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = (time || '00:00').split(':').map(Number);
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0).getTime();
}

const same = (a: any, b: any) => a != null && b != null && String(a) === String(b);

/** Is the member one of the four players in a planned/single match? */
export function isMemberInMatch(m: any, memberId: any): boolean {
  if (!m || memberId == null) return false;
  const ids = [m.team1?.p1Id, m.team1?.p2Id, m.team2?.p1Id, m.team2?.p2Id];
  return ids.some((id) => same(id, memberId));
}

/** 1 or 2 for the team the member is on, else null. */
export function memberTeam(m: any, memberId: any): 1 | 2 | null {
  if (!m || memberId == null) return null;
  if (same(m.team1?.p1Id, memberId) || same(m.team1?.p2Id, memberId)) return 1;
  if (same(m.team2?.p1Id, memberId) || same(m.team2?.p2Id, memberId)) return 2;
  return null;
}

/** Loose name/e-mail match against a booking's free-text bookedBy. */
export function bookingMatchesMember(b: any, member: MemberLike | null): boolean {
  if (!b || !member) return false;
  // ── THE SERVER'S ANSWER WINS WHERE THERE IS ONE ──────────────────
  //
  // `/member/*` marks every booking `mine`, decided by `ownsBooking` against the
  // identity RELATION — a player uuid, not a name. That is the authoritative
  // answer to a question this function can only guess at, and guessing is how a
  // member whose display name was edited stops seeing their own bookings.
  //
  // The text match below stays for the CLUB path, which sends no `mine`, and for
  // legacy rows written before the relation existed.
  if (typeof b.mine === 'boolean') return b.mine;
  const by = b.bookedBy || {};
  const norm = (s: any) =>
    String(s || '')
      .trim()
      .toLowerCase();
  if (member.email && by.email && norm(by.email) === norm(member.email)) return true;
  if (member.name && by.name && norm(by.name) === norm(member.name)) return true;
  return false;
}

export interface MemberBooking {
  booking: any;
  /** How the member is linked: a planned match, an activity, or their own name. */
  via: 'plan' | 'activity' | 'name';
  plan?: any;
  activity?: any;
  startMs: number;
  endMs: number;
}

/**
 * Every booking the member is part of, deduplicated (a booking reachable both
 * through a plan and by name is listed once, with the most specific link kept:
 * plan > activity > name). Sorted by start time.
 */
export function memberBookings({
  bookings = [],
  plannedMatches = [],
  activities = [],
  member,
}: {
  bookings?: any[];
  plannedMatches?: any[];
  activities?: any[];
  member: MemberLike | null;
}): MemberBooking[] {
  if (!member) return [];
  const byId = new Map<any, MemberBooking>();
  const rank = { plan: 3, activity: 2, name: 1 } as const;
  const put = (mb: MemberBooking) => {
    const cur = byId.get(mb.booking.id);
    if (!cur || rank[mb.via] > rank[cur.via]) byId.set(mb.booking.id, mb);
  };
  const wrap = (b: any, via: MemberBooking['via'], extra: any = {}): MemberBooking => ({
    booking: b,
    via,
    startMs: localMs(b.date, b.startTime),
    endMs: localMs(b.date, b.endTime || b.startTime),
    ...extra,
  });

  // 1) planned single matches
  (plannedMatches || []).forEach((p: any) => {
    if (p.status === 'cancelled' || p.status === 'completed') return;
    if (!isMemberInMatch(p, member.id)) return;
    const b = bookings.find((x: any) => x.id === p.bookingId);
    if (b) put(wrap(b, 'plan', { plan: p }));
  });
  // 2) activity registrations → the activity's courts
  (activities || []).forEach((a: any) => {
    if (a.status === 'cancelled') return;
    if (!memberRegistration(a, member.id)) return;
    bookings
      .filter((x: any) => x.seriesId && x.seriesId === a.id)
      .forEach((b: any) => put(wrap(b, 'activity', { activity: a })));
  });
  // 3) the member's own name / e-mail
  bookings.forEach((b: any) => {
    if (bookingMatchesMember(b, member)) put(wrap(b, 'name'));
  });

  return [...byId.values()].sort((a, b) => a.startMs - b.startMs);
}

export function splitUpcomingPast<T extends { startMs: number; endMs?: number }>(
  items: T[],
  nowMs: number = Date.now(),
): { upcoming: T[]; past: T[] } {
  const upcoming: T[] = [];
  const past: T[] = [];
  items.forEach((it) => {
    const end = it.endMs ?? it.startMs;
    if (Number.isNaN(it.startMs)) return;
    if (end >= nowMs) upcoming.push(it);
    else past.push(it);
  });
  upcoming.sort((a, b) => a.startMs - b.startMs);
  past.sort((a, b) => b.startMs - a.startMs);
  return { upcoming, past };
}

export interface MemberSingleMatch {
  match: any;
  myTeam: 1 | 2;
  won: boolean | null;
  /** The ELO delta applied to the member (team change), if recorded. */
  eloChange: number | null;
  playedAt: number;
}

/** Played single matches the member took part in, newest first. */
export function memberSingleMatches(singleMatches: any[] = [], memberId: any): MemberSingleMatch[] {
  if (memberId == null) return [];
  return (singleMatches || [])
    .filter((m: any) => isMemberInMatch(m, memberId))
    .map((m: any) => {
      const myTeam = memberTeam(m, memberId) as 1 | 2;
      const r = m.result || {};
      // winner may be recorded as 1/2 or 't1'/'t2' depending on the writer.
      const w = r.winner;
      const winnerTeam = w === 1 || w === 't1' ? 1 : w === 2 || w === 't2' ? 2 : null;
      const won = winnerTeam == null ? null : winnerTeam === myTeam;
      const change = myTeam === 1 ? r.t1Change : r.t2Change;
      return {
        match: m,
        myTeam,
        won,
        eloChange: typeof change === 'number' ? change : null,
        playedAt: m.playedAt || m.createdAt || 0,
      };
    })
    .sort((a, b) => b.playedAt - a.playedAt);
}

export interface MemberActivity {
  activity: any;
  registration: any | null;
  startMs: number;
  endMs: number;
}

// ── When / where an activity happens ────────────────────────────────
// Ported from ActivitiesView (actStartMs / venueOf / the «when-where» label)
// so the app agrees with the desktop list: the calendar bookings win, then a
// standalone time window, then the config's own date, then an old Padel24
// snapshot. No date at all = «Tid ikke satt» (and it sorts last).

/** Epoch ms when the activity starts; Infinity when no time is set yet. */
export function activityStartMs(a: any, bookings: any[] = []): number {
  const w = activityWhen(a, bookings);
  if (!w.date) return Infinity;
  const ms = localMs(w.date, w.start);
  return Number.isNaN(ms) ? Infinity : ms;
}

/** Epoch ms when the activity ends (the start when no end time is set). */
export function activityEndMs(a: any, bookings: any[] = []): number {
  const w = activityWhen(a, bookings);
  if (!w.date) return Infinity;
  const ms = localMs(w.date, w.end || w.start);
  return Number.isNaN(ms) ? Infinity : ms;
}

/** «i dag · 18:00–20:00», «fre 11. sep (2 dager)», or «Tid ikke satt». */
export function activityWhenLabel(a: any, bookings: any[] = [], now: Date = new Date()): string {
  const w = activityWhen(a, bookings);
  if (!w.date) return 'Tid ikke satt';
  const day = relativeDayLabel(w.date, now) + (w.days > 1 ? ` (${w.days} dager)` : '');
  const time = w.start ? (w.end ? `${w.start}–${w.end}` : w.start) : '';
  return [day, time].filter(Boolean).join(' · ');
}

/** The padel centre an activity is held at (the desktop's venueOf rule). */
export function activityVenue(a: any, clubInfo: any = null): string {
  const standalone =
    a?.bookingDraft?.slotMode === 'standalone' && String(a?.bookingDraft?.venueName || '').trim();
  return standalone || clubInfo?.name || 'Padelsenteret';
}

/**
 * Is sign-up open? Mirrors ActivitiesView's statusBadgeOf + actIsClosed:
 * cancelled, locked («confirmed»), started or finished activities are out, and
 * so are ones closed by hand or by the deadline (signupCloseHours before the
 * start, default 24 h). No time set yet = still open.
 */
export function isActivityOpen(a: any, bookings: any[] = [], nowMs: number = Date.now()): boolean {
  // THE RULE ITSELF LIVES IN THE ENGINE (#245) — the server has to apply the
  // same one, or the command accepts a sign-up for an activity the app does
  // not even list. Only «how many minutes until it starts» is computed here,
  // from this clock; the server resolves that in venue-local time instead.
  const start = activityStartMs(a, bookings);
  const minutesUntilStart = start === Infinity ? null : (start - nowMs) / 60000;
  return activitySignupClosedReason(a, minutesUntilStart) == null;
}

/** Activities the member is signed up for (any status), soonest first, unscheduled last. */
export function memberActivities(
  activities: any[] = [],
  memberId: any,
  bookings: any[] = [],
): MemberActivity[] {
  if (memberId == null) return [];
  return (activities || [])
    .map((a: any) => ({ activity: a, registration: memberRegistration(a, memberId) }))
    .filter((x) => x.registration && x.activity.status !== 'cancelled')
    .map((x) => ({
      ...x,
      startMs: activityStartMs(x.activity, bookings),
      endMs: activityEndMs(x.activity, bookings),
    }))
    .sort((a, b) => a.startMs - b.startMs);
}

/** Activities a member can browse and sign up for, soonest first, unscheduled last. */
export function openActivities(
  activities: any[] = [],
  bookings: any[] = [],
  nowMs: number = Date.now(),
): any[] {
  return (activities || [])
    .filter((a: any) => isActivityOpen(a, bookings, nowMs))
    .sort((a: any, b: any) => activityStartMs(a, bookings) - activityStartMs(b, bookings));
}

// ── Padel centres ───────────────────────────────────────────────────
// Today there is one club per install, but a member is meant to play at
// several centres. Until centres are a real entity, a centre IS a venue name:
// the home club plus every standalone venue an OPEN OR UPCOMING activity
// names (owner decision 2026-11-02, #1238). It used to be every venue a
// non-cancelled activity had ever named, so a venue one old activity used
// stayed a centre for ever — and that was the only screen that read the
// club's whole activity history, which the bootstrap now no longer sends.

/** Statuses after which an activity is no longer «upcoming». */
const FINISHED_ACTIVITY_STATUSES = ['cancelled', 'done', 'completed'];

/**
 * Is this activity still ahead (or under way)? Not finished by status, and
 * dated today or later (by the LOCAL calendar, §4 — so an activity with a
 * date and no times counts all day), or its end — calendar bookings first, as
 * `activityWhen` resolves it — not yet past. An activity with no time set has
 * not happened yet, so it counts.
 */
export function isActivityUpcoming(
  a: any,
  bookings: any[] = [],
  nowMs: number = Date.now(),
): boolean {
  if (FINISHED_ACTIVITY_STATUSES.includes(a?.status)) return false;
  const w = activityWhen(a, bookings);
  if (!w.date) return true;
  if (w.date >= todayLocalISO(new Date(nowMs))) return true;
  return activityEndMs(a, bookings) >= nowMs;
}

export interface Center {
  id: string;
  name: string;
  /** The club this install belongs to. */
  isHome: boolean;
  /** Open activities at this centre right now. */
  openCount: number;
}

export function centersFrom(
  activities: any[] = [],
  clubInfo: any = null,
  bookings: any[] = [],
  nowMs: number = Date.now(),
): Center[] {
  const home = clubInfo?.name || 'Padelsenteret';
  const map = new Map<string, Center>();
  const add = (name: string): Center => {
    let c = map.get(name);
    if (!c) {
      c = { id: name, name, isHome: name === home, openCount: 0 };
      map.set(name, c);
    }
    return c;
  };
  add(home);
  (activities || []).forEach((a: any) => {
    const open = isActivityOpen(a, bookings, nowMs);
    // ONLY A CURRENT ACTIVITY NAMES A CENTRE (#1238): open for sign-up, or
    // still ahead. A done or past one no longer keeps its venue listed.
    if (!open && !isActivityUpcoming(a, bookings, nowMs)) return;
    const c = add(activityVenue(a, clubInfo));
    if (open) c.openCount += 1;
  });
  return [...map.values()].sort(
    (a, b) => Number(b.isHome) - Number(a.isHome) || a.name.localeCompare(b.name, 'nb'),
  );
}

export type NextItem =
  | { kind: 'booking'; startMs: number; item: MemberBooking }
  | { kind: 'activity'; startMs: number; item: MemberActivity };

/** The single nearest upcoming thing for the member, across bookings and activities. */
export function nextForMember(
  bookings: MemberBooking[],
  activities: MemberActivity[],
  nowMs: number = Date.now(),
): NextItem | null {
  const cands: NextItem[] = [];
  bookings.forEach((b) => {
    if (b.endMs >= nowMs) cands.push({ kind: 'booking', startMs: b.startMs, item: b });
  });
  activities.forEach((a) => {
    if (a.endMs >= nowMs) cands.push({ kind: 'activity', startMs: a.startMs, item: a });
  });
  cands.sort((a, b) => a.startMs - b.startMs);
  return cands[0] || null;
}

/** «i dag», «i morgen», or a short Norwegian date. */
export function relativeDayLabel(date: string, now: Date = new Date()): string {
  const today = todayLocalISO(now);
  if (date === today) return 'i dag';
  const t = new Date(now);
  t.setDate(t.getDate() + 1);
  if (date === todayLocalISO(t)) return 'i morgen';
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('nb-NO', { weekday: 'short', day: 'numeric', month: 'short' });
}

// ── Live tournaments ────────────────────────────────────────────────
// A tournament in progress is the most relevant thing for a member. The
// history helper (engine getPlayerTournamentHistory over archiveWithLive)
// already includes live entries; here we keep the ones still running and
// split the member's matches into open / played. Match shapes differ per
// format (Padel24 slots vs flat ids vs KM pairs), so the small accessors
// below hide that from the screens.

/** Player ids on one side of a match — Padel24 slots or flat p1Id/p2Id. */
export function teamPlayerIds(team: any): any[] {
  if (!team) return [];
  return [team.p1?.playerId ?? team.p1Id, team.p2?.playerId ?? team.p2Id].filter((x) => x != null);
}

/** Which side (1/2) the member plays on in a tournament match, else null. */
export function matchSideOf(m: any, memberId: any): 1 | 2 | null {
  if (!m || memberId == null) return null;
  if (teamPlayerIds(m.team1).some((id) => same(id, memberId))) return 1;
  if (teamPlayerIds(m.team2).some((id) => same(id, memberId))) return 2;
  return null;
}

/** Has a result been recorded? KM keeps `score`, the others `completed`. */
export function isMatchCompleted(m: any): boolean {
  if (!m) return false;
  if (typeof m.completed === 'boolean') return m.completed;
  return !!m.score;
}

export interface LiveTournament {
  key: string;
  tournament: any;
  type: string;
  /** The member's matches (from the history helper). */
  matches: any[];
  open: any[];
  played: any[];
  /** false while the tournament is still in «ready» (preview) — no results yet. */
  started: boolean;
}

export function memberLiveTournaments(history: any[] = []): LiveTournament[] {
  return (history || [])
    .filter((h: any) => {
      const t = h?.tournament;
      return t && t.isLive && !t.isArchived && t.status !== 'done';
    })
    .map((h: any) => {
      const t = h.tournament;
      const matches = h.matches || [];
      return {
        key: h.key,
        tournament: t,
        type: t.type || 'padel24',
        matches,
        open: matches.filter((m: any) => !isMatchCompleted(m)),
        played: matches.filter((m: any) => isMatchCompleted(m)),
        started: t.status !== 'ready',
      };
    });
}

// ── Planned single matches with open slots ──────────────────────────
// A plan has four slots (team1.p1/p2, team2.p1/p2). The desktop always fills
// all four; the mobile app lets a booker register a match with EMPTY slots
// (null ids) that other members can join. These helpers are the only place
// that knows the slot layout.

export type PlanSlot = { team: 1 | 2; pos: 'p1Id' | 'p2Id'; playerId: any | null };

export function planSlots(plan: any): PlanSlot[] {
  return [
    { team: 1, pos: 'p1Id', playerId: plan?.team1?.p1Id ?? null },
    { team: 1, pos: 'p2Id', playerId: plan?.team1?.p2Id ?? null },
    { team: 2, pos: 'p1Id', playerId: plan?.team2?.p1Id ?? null },
    { team: 2, pos: 'p2Id', playerId: plan?.team2?.p2Id ?? null },
  ];
}

export function planOpenSlots(plan: any): number {
  return planSlots(plan).filter((s) => s.playerId == null).length;
}

/** New team objects with the member in the first empty slot, or null when full / already in. */
export function planJoin(plan: any, memberId: any): { team1: any; team2: any } | null {
  if (memberId == null || isMemberInMatch(plan, memberId)) return null;
  const slot = planSlots(plan).find((s) => s.playerId == null);
  if (!slot) return null;
  const team1 = { ...(plan.team1 || {}) };
  const team2 = { ...(plan.team2 || {}) };
  (slot.team === 1 ? team1 : team2)[slot.pos] = memberId;
  return { team1, team2 };
}

/** New team objects with the member's slot emptied, or null when not in the match. */
export function planLeave(plan: any, memberId: any): { team1: any; team2: any } | null {
  if (!isMemberInMatch(plan, memberId)) return null;
  const team1 = { ...(plan.team1 || {}) };
  const team2 = { ...(plan.team2 || {}) };
  planSlots(plan).forEach((s) => {
    if (same(s.playerId, memberId)) (s.team === 1 ? team1 : team2)[s.pos] = null;
  });
  return { team1, team2 };
}

/** New team objects with one slot set (or emptied with null). Never mutates. */
export function planSetSlot(
  plan: any,
  team: 1 | 2,
  pos: 'p1Id' | 'p2Id',
  playerId: any | null,
): { team1: any; team2: any } {
  const team1 = { ...(plan?.team1 || {}) };
  const team2 = { ...(plan?.team2 || {}) };
  (team === 1 ? team1 : team2)[pos] = playerId;
  return { team1, team2 };
}

/**
 * The clubs a member belongs to, as { id, name } — what the automatic message
 * groups («Nivå N · klubb», «Alle · klubb») are keyed on. Today an install
 * serves ONE club, so this is the club itself with a stable id; when roles and
 * multi-club memberships land (tenant roster), this returns the roster and
 * every group id follows the tenant id instead.
 */
export const HOME_CLUB_ID = 'home';
export function memberClubsOf(clubInfo: any): { id: string; name: string }[] {
  const name = String(clubInfo?.name || '').trim() || 'Padelsenteret';
  return [{ id: HOME_CLUB_ID, name }];
}

/**
 * WHOSE ANSWER THE MEMBER'S HISTORY IS (#185).
 *
 * `/member/bootstrap` returns it already computed and field-filtered, so one
 * member's payload carries no other member's details. Deriving it again in the
 * browser is not merely wasteful — it is impossible: the engine needs the whole
 * tournament documents, and a member is deliberately not given them. Running it
 * anyway produced an EMPTY history from a payload that already had the answer.
 *
 * So the server's answer wins when there is one, and the local derivation stays
 * for the club-side surfaces that legitimately have an archive to derive from.
 */
export function memberTournamentHistory(fromServer: any[] | undefined, derive: () => any[]): any[] {
  return fromServer && fromServer.length > 0 ? fromServer : derive();
}

// ─────────────────────────────────────────────────────────────────────
// EQUIPMENT BESIDE THE COURTS (docs/domains/ASSET_BOOKING.md, slice 2).
//
// The member projection answers `mine` for equipment rows the way it does for
// bookings — the viewer's own, or on the viewer's own court booking — so
// these are filters over the server's answer, never a name match.
// ─────────────────────────────────────────────────────────────────────

export interface MemberEquipmentBooking {
  assetBooking: any;
  startMs: number;
  endMs: number;
}

/**
 * The member's STANDALONE equipment bookings — a machine booked on its own —
 * sorted by start. Rows riding with a court booking are shown on that booking
 * (`attachedEquipment`) rather than listed twice.
 */
export function memberEquipmentBookings(assetBookings: any[] = []): MemberEquipmentBooking[] {
  return (assetBookings || [])
    .filter((ab: any) => ab && ab.mine === true && !ab.bookingId)
    .map((ab: any) => ({
      assetBooking: ab,
      startMs: localMs(ab.date, ab.startTime),
      endMs: localMs(ab.date, ab.endTime || ab.startTime),
    }))
    .sort((a, b) => a.startMs - b.startMs);
}

/** The equipment riding with one court booking, by the booking's `book_…` id. */
export function attachedEquipment(assetBookings: any[] = [], bookingId: any): any[] {
  if (bookingId == null) return [];
  return (assetBookings || []).filter((ab: any) => ab && same(ab.bookingId, bookingId));
}

/** The asset's name, or the id when the registry no longer lists it. */
export function assetNameFor(assets: any[] = [], assetId: any): string {
  const asset = (assets || []).find((a: any) => same(a?.id, assetId));
  return String(asset?.name || assetId || '');
}
